/**
 * Mine vurderinger — detaljside (Fase A).
 *
 * Unified rendering for SEB-prøver, prosjekt-innleveringer og kompositt.
 * Deep-link fra e-post: /assessments/:id (login-flow støtter ?next=...).
 *
 * Viser:
 *  - Overall-resultat (bestått/ikke bestått, karakter, score, sensor-rationale)
 *  - Per komponent: type, vekt, sensor-navn, karakter, score, kommentar, egne filer
 *  - Arkiver/fjern-arkiver
 *  - Ingen KI-preview, ingen klage-flyt, ingen PDF
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  CheckCircle2,
  XCircle,
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Calendar,
  Building2,
  User,
  Download,
  FileText,
  AlertCircle,
  Loader2,
  Info,
  EyeOff,
} from 'lucide-react';
// Deep-link auth: ved 401 send til login med returnTo=/assessments/:id for sømløs retur
function redirectToLogin(): void {
  const current = window.location.pathname + window.location.search;
  window.location.href = `/?returnTo=${encodeURIComponent(current)}`;
}

interface Component {
  component_id: string;
  type: string;
  title: string;
  description: string | null;
  sequence: number;
  weight_pct: number | null;
  grading_scale: string;
  pass_threshold: number | null;
  is_anonymous: boolean;
  submission: {
    id: string;
    status: string;
    submitted_at: string | null;
    grade: string | null;
    score: number | null;
    passed: boolean | null;
    sensor_comment: string | null;
    graded_at: string | null;
    sensor_name: string | null;
    file_count: number;
    file_names: string[];
    alternative_assessment_used: boolean;
    original_type: string | null;
  } | null;
}

interface Assessment {
  id: string;
  candidate_name: string;
  candidate_code: string | null;
  course_instance_id: string;
  course_name: string;
  study_plan_title: string;
  study_plan_description: string | null;
  organization_name: string | null;
  organization_email: string | null;
  model: 'single_component' | 'weighted_composite' | 'all_required_composite';
  overall: {
    passed: boolean;
    grade: string | null;
    score: number | null;
    calculated_score: number | null;
    rationale: string | null;
    finalized_at: string;
    finalized_by_name: string | null;
  };
  components: Component[];
  archived_at: string | null;
}

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('nb-NO', {
      timeZone: 'Europe/Oslo',
      dateStyle: 'long',
      timeStyle: 'short',
    });
  } catch { return iso; }
}

function typeLabel(t: string): string {
  const m: Record<string, string> = {
    written: 'Skriftlig prøve',
    oral: 'Muntlig',
    project: 'Prosjekt',
    portfolio: 'Mappe',
    reflection: 'Refleksjon',
    practical: 'Praktisk prøve',
    observation: 'Observasjon',
    group: 'Gruppearbeid',
  };
  return m[t] || t;
}

function modelLabel(m: string): string {
  if (m === 'single_component') return 'Enkel vurdering';
  if (m === 'weighted_composite') return 'Kompositt (vektet)';
  if (m === 'all_required_composite') return 'Kompositt (alle må bestås)';
  return m;
}

export function AssessmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function load() {
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const res = await fetch(`/api/candidate/assessments/${id}`, { credentials: 'include' });
      if (res.status === 401) { redirectToLogin(); return; }
      if (res.status === 404) {
        setNotFound(true);
        return;
      }
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'Kunne ikke hente vurdering');
        return;
      }
      setAssessment(data.assessment);
    } catch (err) {
      console.error(err);
      setError('Kunne ikke hente vurdering');
    } finally {
      setLoading(false);
    }
  }

  async function toggleArchive() {
    if (!assessment) return;
    try {
      const url = `/api/candidate/assessments/${assessment.id}/archive`;
      const method = assessment.archived_at ? 'DELETE' : 'POST';
      const res = await fetch(url, { method, credentials: 'include' });
      if (res.status === 401) { redirectToLogin(); return; }
      if (!res.ok) throw new Error('Kunne ikke endre arkiv-status');
      await load();
    } catch (err) {
      console.error(err);
      alert('Kunne ikke endre arkiv-status');
    }
  }

  function downloadFile(componentId: string, index: number) {
    if (!assessment) return;
    const url = `/api/candidate/assessments/${assessment.id}/file/${componentId}/${index}`;
    window.open(url, '_blank');
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-kf-blue-600" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center bg-white rounded-lg border border-kf-gray-200 p-10">
          <AlertCircle className="w-16 h-16 text-kf-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-kf-gray-900 mb-2">Fant ikke vurderingen</h1>
          <p className="text-kf-gray-600 mb-6">
            Vurderingen finnes ikke, eller den er ikke ferdig sensurert ennå.
          </p>
          <Link
            to="/assessments"
            className="inline-flex items-center gap-2 px-4 py-2 bg-kf-blue-600 text-white rounded hover:bg-kf-blue-700"
          >
            <ArrowLeft className="w-4 h-4" /> Tilbake til oversikt
          </Link>
        </div>
      </div>
    );
  }

  if (error || !assessment) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="p-4 bg-kf-error-50 border border-kf-error-200 rounded-lg text-kf-error-800">
          {error || 'Ukjent feil'}
        </div>
      </div>
    );
  }

  const isComposite = assessment.model !== 'single_component';

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 md:py-8">
      <div className="mb-4">
        <Link
          to="/assessments"
          className="inline-flex items-center gap-1 text-sm text-kf-gray-600 hover:text-kf-gray-800"
        >
          <ArrowLeft className="w-4 h-4" /> Tilbake til oversikt
        </Link>
      </div>

      {/* Header */}
      <div className="bg-white rounded-lg border border-kf-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-3">
            {assessment.overall.passed ? (
              <div className="p-3 bg-kf-success-100 rounded-lg">
                <CheckCircle2 className="w-7 h-7 text-kf-success-700" />
              </div>
            ) : (
              <div className="p-3 bg-kf-error-100 rounded-lg">
                <XCircle className="w-7 h-7 text-kf-error-700" />
              </div>
            )}
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-kf-gray-900">{assessment.course_name}</h1>
              {assessment.study_plan_title && assessment.study_plan_title !== assessment.course_name && (
                <p className="text-sm text-kf-gray-600 mt-1">{assessment.study_plan_title}</p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-kf-gray-600">
                {assessment.organization_name && (
                  <span className="inline-flex items-center gap-1">
                    <Building2 className="w-4 h-4" /> {assessment.organization_name}
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> {formatDate(assessment.overall.finalized_at)}
                </span>
                <span>{modelLabel(assessment.model)}</span>
              </div>
            </div>
          </div>
          <button
            onClick={toggleArchive}
            className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-sm text-kf-gray-700 hover:text-kf-gray-900 hover:bg-kf-gray-100 rounded border border-kf-gray-300"
            title={assessment.archived_at ? 'Fjern fra arkiv' : 'Arkiver evig'}
          >
            {assessment.archived_at ? (
              <><ArchiveRestore className="w-4 h-4" /> Fjern fra arkiv</>
            ) : (
              <><Archive className="w-4 h-4" /> Arkiver</>
            )}
          </button>
        </div>

        {/* Overall resultat-boks */}
        <div className={`p-4 rounded-lg border ${
          assessment.overall.passed
            ? 'bg-kf-success-50 border-kf-success-200'
            : 'bg-kf-error-50 border-kf-error-200'
        }`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-sm font-medium text-kf-gray-600 mb-1">Resultat</div>
              <div className={`text-2xl font-bold ${
                assessment.overall.passed ? 'text-kf-success-800' : 'text-kf-error-800'
              }`}>
                {assessment.overall.passed ? 'Bestått' : 'Ikke bestått'}
              </div>
              {assessment.overall.grade && (
                <div className="text-sm text-kf-gray-700 mt-1">Karakter: {assessment.overall.grade}</div>
              )}
              {assessment.overall.score !== null && (
                <div className="text-sm text-kf-gray-700">Score: {assessment.overall.score}</div>
              )}
            </div>
            {assessment.overall.finalized_by_name && (
              <div className="text-sm text-kf-gray-600">
                <div className="flex items-center gap-1 mb-0.5">
                  <User className="w-4 h-4" />
                  <span className="font-medium">Finalisert av</span>
                </div>
                <div>{assessment.overall.finalized_by_name}</div>
              </div>
            )}
          </div>
          {assessment.overall.rationale && (
            <div className="mt-3 pt-3 border-t border-kf-gray-200">
              <div className="text-sm font-medium text-kf-gray-700 mb-1">Sensors begrunnelse</div>
              <p className="text-sm text-kf-gray-700 whitespace-pre-wrap">{assessment.overall.rationale}</p>
            </div>
          )}
        </div>
      </div>

      {/* Komponent-detaljer */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-kf-gray-800">
          {isComposite ? `${assessment.components.length} komponenter` : 'Vurdering'}
        </h2>

        {assessment.components.map((c) => (
          <div key={c.component_id} className="bg-white rounded-lg border border-kf-gray-200 p-5">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-kf-gray-900">{c.title}</h3>
                  <span className="text-xs px-2 py-0.5 bg-kf-blue-50 text-kf-blue-700 rounded border border-kf-blue-200">
                    {typeLabel(c.type)}
                  </span>
                  {isComposite && c.weight_pct !== null && (
                    <span className="text-xs text-kf-gray-600">({c.weight_pct}% vekt)</span>
                  )}
                  {c.is_anonymous && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-kf-gray-100 text-kf-gray-700 rounded border border-kf-gray-300">
                      <EyeOff className="w-3 h-3" /> Anonymt vurdert
                    </span>
                  )}
                </div>
                {c.description && (
                  <p className="text-sm text-kf-gray-600 mt-1">{c.description}</p>
                )}
              </div>
              {c.submission?.passed === true && (
                <CheckCircle2 className="w-5 h-5 text-kf-success-600 flex-shrink-0" />
              )}
              {c.submission?.passed === false && (
                <XCircle className="w-5 h-5 text-kf-error-600 flex-shrink-0" />
              )}
            </div>

            {c.submission ? (
              <div className="space-y-3">
                {/* Sensor-detaljer */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  {c.submission.sensor_name && (
                    <div>
                      <div className="text-kf-gray-500 text-xs">Sensor</div>
                      <div className="text-kf-gray-800 font-medium">{c.submission.sensor_name}</div>
                    </div>
                  )}
                  {c.submission.graded_at && (
                    <div>
                      <div className="text-kf-gray-500 text-xs">Sensurert</div>
                      <div className="text-kf-gray-800">{formatDate(c.submission.graded_at)}</div>
                    </div>
                  )}
                  {c.submission.grade && (
                    <div>
                      <div className="text-kf-gray-500 text-xs">Karakter</div>
                      <div className="text-kf-gray-800 font-medium">{c.submission.grade}</div>
                    </div>
                  )}
                  {c.submission.score !== null && (
                    <div>
                      <div className="text-kf-gray-500 text-xs">Score</div>
                      <div className="text-kf-gray-800 font-medium">
                        {c.submission.score}
                        {c.pass_threshold !== null && ` (grense: ${c.pass_threshold})`}
                      </div>
                    </div>
                  )}
                </div>

                {c.submission.alternative_assessment_used && (
                  <div className="p-3 bg-kf-purple-50 border border-kf-purple-200 rounded text-sm text-kf-purple-900">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>Alternativ vurderingsform ble brukt{c.submission.original_type ? ` (opprinnelig planlagt som ${typeLabel(c.submission.original_type)})` : ''}.</span>
                    </div>
                  </div>
                )}

                {c.submission.sensor_comment && (
                  <div className="p-3 bg-kf-blue-50 border border-kf-blue-200 rounded">
                    <div className="text-xs font-medium text-kf-blue-900 mb-1">Sensors kommentar</div>
                    <p className="text-sm text-kf-gray-800 whitespace-pre-wrap">{c.submission.sensor_comment}</p>
                  </div>
                )}

                {/* Dine filer — kun for ikke-anonyme komponenter med filer */}
                {!c.is_anonymous && c.submission.file_names.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-kf-gray-700 mb-2">Dine innleverte filer</div>
                    <div className="space-y-1">
                      {c.submission.file_names.map((name, i) => (
                        <button
                          key={i}
                          onClick={() => downloadFile(c.component_id, i)}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-kf-gray-50 hover:bg-kf-gray-100 rounded text-sm text-left border border-kf-gray-200"
                        >
                          <span className="inline-flex items-center gap-2 min-w-0">
                            <FileText className="w-4 h-4 text-kf-gray-500 flex-shrink-0" />
                            <span className="truncate">{name}</span>
                          </span>
                          <Download className="w-4 h-4 text-kf-blue-600 flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-kf-gray-500 italic">Ingen innlevering registrert.</div>
            )}
          </div>
        ))}
      </div>

      {/* Hjelpe-info nederst */}
      <div className="mt-8 p-4 bg-kf-gray-50 border border-kf-gray-200 rounded-lg text-sm text-kf-gray-700">
        <div className="flex items-start gap-2">
          <Info className="w-5 h-5 flex-shrink-0 mt-0.5 text-kf-gray-500" />
          <div>
            <p className="font-medium mb-1">Spørsmål om vurderingen?</p>
            <p>
              Kontakt {assessment.organization_name || 'kursarrangøren'}
              {assessment.organization_email && (
                <> på <a href={`mailto:${assessment.organization_email}`} className="text-kf-blue-600 hover:underline">{assessment.organization_email}</a></>
              )}.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
